import React, { useState } from 'react'
import { SortOption } from '@e3dc-react/shell/Elements/Sort'
import { CurrentSort } from '@e3dc-react/shell/Elements/Table'
import { Box, Button, IconButton, Stack, SvgIcon, Typography } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ReactComponent as SortIcon } from '../icons/svgs/mobile_sort_icon.svg'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import { makeStyles } from 'tss-react/mui'
import { MobileDrawer } from 'src/Elements/Drawer'
import { J2Multiply } from '@e3dc-react/icons'

const useStyles = makeStyles()(theme => ({
  filterButton: {
    borderRadius: theme.spacing(2),
    height: theme.shape.filter.height,
    aspectRatio: 1,
    minWidth: 'unset',
  },
  title: {
    fontSize: '24px',
    fontWeight: 400,
    lineHight: 'normal',
  },
  sortItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: theme.spacing(2),
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    marginBottom: theme.spacing(2),
    background: theme.palette.shades.shade04,
  },
}))

interface MobileSortingProps<T> {
  onChange: (sort: CurrentSort<T>) => void
  value: CurrentSort<T>
  defaultSortValue: CurrentSort<T>
  sortOptions: SortOption<T>[]
  dataQa?: string
}

/**
 * Component button for sorting on mobile screens.
 * It displays and hold as container, button for opening mobile drawer with all other sorting items.
 */
export const MobileSorting = <T,>({ sortOptions, value, onChange, dataQa, defaultSortValue }: MobileSortingProps<T>): React.ReactElement => {
  const [selectedSort, setSelectedSort] = useState<CurrentSort<T>>(value)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const shouldDisableApplySortButton = value.name === selectedSort.name && value.direction === selectedSort.direction
  const shouldDisableResetSortButton = defaultSortValue.name === selectedSort.name && defaultSortValue.direction === selectedSort.direction

  const handleDrawerOpen = (): void => {
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = (): void => {
    setSelectedSort({ name: value.name, direction: value.direction })
    setIsDrawerOpen(false)
  }

  const handleApplySort = (): void => {
    onChange({ name: selectedSort.name, direction: selectedSort.direction })
    setIsDrawerOpen(false)
  }

  const handleResetSort = (): void => {
    onChange({ name: defaultSortValue.name, direction: defaultSortValue.direction })
    setSelectedSort({ name: defaultSortValue.name, direction: defaultSortValue.direction })
    setIsDrawerOpen(false)
  }

  return (
    <>
      <Button variant="contained" data-qa={dataQa} className={classes.filterButton} onClick={handleDrawerOpen} disableElevation>
        <SvgIcon component={SortIcon} inheritViewBox sx={{ fontSize: 32 }} />
      </Button>

      <MobileDrawer onClose={handleDrawerClose} open={isDrawerOpen}>
        <Stack flexDirection="row" justifyContent="space-between" alignItems="center" pb={2}>
          <Typography className={classes.title}>{t('emobility.sort.title')}</Typography>
          <IconButton onClick={handleDrawerClose} color="inherit" size="large">
            <J2Multiply fontSize="inherit" />
          </IconButton>
        </Stack>

        <Stack direction="column" justifyContent="space-between" overflow="auto" pb={8}>
          <Stack overflow="scroll">
            {sortOptions.map(option => {
              const isItemSelected = option.value === selectedSort.name
              const isSelectedItemAscending = isItemSelected && selectedSort.direction === 'asc'
              const isSelectedItemDescending = isItemSelected && selectedSort.direction === 'desc'

              return (
                <Box
                  key={`${String(option.value)}-${option.label}`}
                  className={classes.sortItemContainer}
                  sx={theme => ({ border: isItemSelected ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.shades.shade03}` })}
                >
                  <Typography variant="h3">{option.label}</Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      disableElevation
                      variant={isSelectedItemAscending ? 'contained' : 'outlined'}
                      className={classes.filterButton}
                      onClick={() => setSelectedSort({ name: option.value, direction: 'asc' })}
                    >
                      {/* for some reason, the icon color is not applied automatically just inside this drawer, so we force it to "inherit" Button color as it should */}
                      <ArrowDropUp sx={{ fontSize: 32, color: 'inherit !important' }} />
                    </Button>
                    <Button
                      disableElevation
                      variant={isSelectedItemDescending ? 'contained' : 'outlined'}
                      className={classes.filterButton}
                      onClick={() => setSelectedSort({ name: option.value, direction: 'desc' })}
                    >
                      {/* for some reason, the icon color is not applied automatically just inside this drawer, so we force it to "inherit" Button color as it should */}
                      <ArrowDropDown sx={{ fontSize: 32, color: 'inherit !important' }} />
                    </Button>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={theme => ({
              py: 2,
              px: 4,
              mx: -2,
              borderTop: `1px solid ${theme.palette.divider}`,
              position: 'fixed',
              bottom: 0,
              width: '100%',
              backgroundColor: 'background.paper',
            })}
          >
            <Button fullWidth variant="outlined" color="secondary" onClick={handleResetSort} disabled={shouldDisableResetSortButton}>
              {t('common.reset')}
            </Button>
            <Button fullWidth variant="contained" onClick={handleApplySort} disabled={shouldDisableApplySortButton}>
              {t('common.apply')}
            </Button>
          </Stack>
        </Stack>
      </MobileDrawer>
    </>
  )
}
